// Programmatic-SEO data for the Ernest "Industries" landing pages.
// One entry per brand vertical. Each page must carry genuinely differentiated,
// substantive content (not a template with one word swapped): that's what
// earns citations from search + AI answer engines and avoids doorway penalties.
//
// Slugs are permanent once indexed (changing them loses acquired SEO). Add new
// industries by appending an entry; the route, sitemap, dropdown and hub pick
// it up automatically.

export interface SectorActivation {
  title: string;
  body: string;
}

export interface SectorFaq {
  q: string;
  a: string;
}

export interface BrandSector {
  slug: string;
  /** Short label for nav dropdown / hub cards, e.g. "Sport & Outdoor". */
  label: string;
  /** Kicker shown above the H1. */
  eyebrow: string;
  metaTitle: string;
  metaDescription: string;
  h1: string;
  /** Hero subtext / direct-answer opening, extracted by AI answer engines. */
  intro: string;
  whyHeading: string;
  whyBody: string[];
  painPoints: string[];
  communityHeading: string;
  communityTypes: string[];
  activationHeading: string;
  activations: SectorActivation[];
  /** Illustrative brand types for this industry (kept generic, editable). */
  exampleBrandsLine: string;
  faq: SectorFaq[];
}

export const BRAND_SECTORS: BrandSector[] = [
  {
    slug: "sport",
    label: "Sport & Outdoor",
    eyebrow: "ernest for sport brands",
    metaTitle: "Community marketing for sport & outdoor brands · ernest",
    metaDescription:
      "ernest connects sport and outdoor brands with the IRL communities their customers already belong to: run clubs, trail, cycling, climbing. Gifting, event sponsorship, ambassadors, content.",
    h1: "Community marketing for sport & outdoor brands",
    intro:
      "Sport and outdoor brands reach their next customers where they already train: run clubs, trail crews, cycling groups, climbing gyms. ernest connects you to these IRL communities and gives you the workflow to run community sponsorships: gifting, event sponsorship, ambassador and content activations, then measure real impact.",
    whyHeading: "Why sport is won inside communities",
    whyBody: [
      "A runner who shows up every week to their run club is a far stronger usage signal than a like. Sport communities gather exactly the regular practitioners your products target, in a context of trust and peer recommendation.",
      "Where paid media fades, a well-run presence in these clubs buys conversation time, real product trials and word of mouth: the way running shoes, technical apparel and sports nutrition actually get bought.",
    ],
    painPoints: [
      "Your paid performance is plateauing while acquisition costs climb.",
      "Your technical products need to be tried to convince, not just seen.",
      "You want field proof and authentic content, not generic sponsored posts.",
    ],
    communityHeading: "Communities that fit your brand",
    communityTypes: [
      "Run clubs and urban running crews",
      "Trail and hiking clubs",
      "Cycling and gravel groups",
      "Climbing and bouldering gyms",
      "Padel, tennis and racket-sport leagues",
    ],
    activationHeading: "Sponsorship formats that work for sport",
    activations: [
      {
        title: "Gifting & product trials",
        body: "Get club members testing your latest gear (shoes, apparel, nutrition) and collect credible feedback from regular users.",
      },
      {
        title: "Event sponsorship",
        body: "Fund a club outing, race or training camp in exchange for visibility and on-the-ground presence.",
      },
      {
        title: "Ambassadors & content",
        body: "Turn the most engaged organizers and members into ambassadors who create content and recruit by recommendation.",
      },
    ],
    exampleBrandsLine: "Built for running, trail, technical apparel, nutrition and sports-recovery brands.",
    faq: [
      {
        q: "How can a sport brand sponsor a run club?",
        a: "With ernest, you identify clubs whose members match your target, then offer gifting, event funding or an ambassador partnership. Every collaboration is scoped and measured inside the platform.",
      },
      {
        q: "How do you measure the impact of a sport activation?",
        a: "ernest tracks real community metrics: Genuine Interest Reach, cost per genuine interest, word-of-mouth conversion, organic reach and trackable conversions, beyond simple impressions.",
      },
    ],
  },
  {
    slug: "food",
    label: "Food",
    eyebrow: "ernest for food brands",
    metaTitle: "Community marketing for food brands · ernest",
    metaDescription:
      "ernest connects food brands with the IRL communities that cook, taste and gather: supper clubs, cooking classes, food collectives. Gifting, event sponsorship, ambassadors, content.",
    h1: "Community marketing for food brands",
    intro:
      "Food brands get adopted around a table, not in a feed. ernest connects you to the communities that cook, taste and gather (supper clubs, cooking classes, food collectives) so you can run community sponsorships (gifting, event sponsorship, ambassadors), and measure the impact.",
    whyHeading: "Why food is tasted in community",
    whyBody: [
      "Taste travels through shared experience. A product tasted at a dinner among enthusiasts builds conviction and word of mouth that no display impression can match.",
      "Food communities gather tastemakers: the people who test, cook, recommend and set the trends for their circle. It's the ideal channel for a credible product launch.",
    ],
    painPoints: [
      "The shelf is crowded and product trial is the real purchase trigger.",
      "You want credible tastemakers, not impersonal sponsored posts.",
      "You want authentic content and reviews around your products.",
    ],
    communityHeading: "Communities that fit your brand",
    communityTypes: [
      "Supper clubs and enthusiast dinners",
      "Cooking classes and workshops",
      "Food collectives and gastronomy clubs",
      "Diet-based communities (plant-based, healthy, sport)",
      "Farmers' markets and culinary events",
    ],
    activationHeading: "Sponsorship formats that work for food",
    activations: [
      {
        title: "Product seeding & tasting",
        body: "Put your products at the heart of a dinner or workshop and collect feedback from real enthusiasts.",
      },
      {
        title: "Dinner & event sponsorship",
        body: "Fund or co-host a supper club around your brand, with a dedicated tasting experience.",
      },
      {
        title: "Ambassadors & recipes",
        body: "Work with hosts who fold your products into their recipes and create authentic content.",
      },
    ],
    exampleBrandsLine: "Built for specialty grocery, snacking, fresh products, plant-based alternatives and nutrition brands.",
    faq: [
      {
        q: "How do you launch a new food product with a community?",
        a: "With ernest, you place your product at targeted communities' dinners or workshops, turning the launch into a shared tasting that generates credible reviews and word of mouth.",
      },
      {
        q: "What kind of food communities can I activate?",
        a: "Supper clubs, cooking classes, food collectives or diet-based communities: ernest matches you with the ones whose members fit your brand.",
      },
    ],
  },
  {
    slug: "drinks",
    label: "Drinks",
    eyebrow: "ernest for drinks brands",
    metaTitle: "Community marketing for drinks brands · ernest",
    metaDescription:
      "ernest connects drinks brands with the IRL communities that taste together: wine clubs, cocktail & mixology, specialty coffee, sober-curious. Gifting, event sponsorship, ambassadors, content.",
    h1: "Community marketing for drinks brands",
    intro:
      "A drink is discovered in a shared moment. ernest connects you to the communities that taste together (wine clubs, mixology circles, specialty coffee, sober-curious) so you can run community sponsorships (gifting, event sponsorship, ambassadors), and measure the impact.",
    whyHeading: "Why drinks are shared in community",
    whyBody: [
      "Tasting is a social experience: people discover, compare and recommend. Placing your drink in those moments builds a memorable association and natural word of mouth.",
      "Tasting communities gather early adopters, often the first to try a new brand and pass it on to the people around them.",
    ],
    painPoints: [
      "Discovery and the first sip are decisive, and hard to create with paid media.",
      "You're after real consumption occasions, not just visibility.",
      "You want credible tastemakers in a crowded market.",
    ],
    communityHeading: "Communities that fit your brand",
    communityTypes: [
      "Wine clubs and tasting circles",
      "Mixology and cocktail communities",
      "Specialty coffee and barista communities",
      "Sober-curious / no-alcohol communities",
      "Craft-beer clubs and breweries",
    ],
    activationHeading: "Sponsorship formats that work for drinks",
    activations: [
      {
        title: "Tasting & seeding",
        body: "Introduce your drink at a tasting session, with a dedicated moment of experience.",
      },
      {
        title: "Event sponsorship",
        body: "Become the drink of a community event and own a real consumption occasion.",
      },
      {
        title: "Ambassadors & content",
        body: "Work with hosts and enthusiasts who fold your drink into their gatherings and create content.",
      },
    ],
    exampleBrandsLine: "Built for wine, spirits, no-alcohol drinks, coffee, tea and craft-beer brands.",
    faq: [
      {
        q: "How do you get a new drink discovered by a community?",
        a: "With ernest, you host a tasting or sponsor a targeted community's event (a wine club, a mixology circle), creating a real consumption occasion and word of mouth.",
      },
      {
        q: "Can I activate no-alcohol communities?",
        a: "Yes. ernest lists diverse communities, including sober-curious and specialty coffee, to match your brand with the right consumption moments.",
      },
    ],
  },
  {
    slug: "beauty",
    label: "Beauty & Skincare",
    eyebrow: "ernest for beauty brands",
    metaTitle: "Community marketing for beauty & skincare brands · ernest",
    metaDescription:
      "ernest connects beauty and skincare brands with the IRL communities that take care of themselves together: skincare circles, wellness, self-care. Gifting, event sponsorship, ambassadors, content.",
    h1: "Community marketing for beauty & skincare brands",
    intro:
      "Beauty is recommended person to person. ernest connects you to the communities that take care of themselves together (skincare circles, self-care rituals, wellness) so you can run community sponsorships (gifting, ambassadors, events), and measure real impact.",
    whyHeading: "Why beauty is recommended in community",
    whyBody: [
      "The most powerful beauty advice comes from a trusted person who has tried the product. Self-care and skincare communities are exactly those circles of recommendation.",
      "Letting people try a product in a trusted setting generates sincere reviews and authentic content, far more convincing than an ad, especially with savvy audiences.",
    ],
    painPoints: [
      "The beauty audience is saturated with sponsored content and increasingly skeptical.",
      "A real trial and a trusted person's opinion are decisive.",
      "You want authentic, varied content, not one more placement.",
    ],
    communityHeading: "Communities that fit your brand",
    communityTypes: [
      "Skincare and self-care circles",
      "Wellness and well-being communities",
      "Clean / natural beauty collectives",
      "Yoga, pilates and women's fitness groups",
      "Women's lifestyle communities",
    ],
    activationHeading: "Sponsorship formats that work for beauty",
    activations: [
      {
        title: "Gifting & product trials",
        body: "Let community members try your skincare and collect sincere reviews from real users.",
      },
      {
        title: "Workshops & events",
        body: "Sponsor or host a self-care moment around your products, with a memorable experience.",
      },
      {
        title: "Ambassadors & content",
        body: "Work with trusted hosts who recommend and create authentic content.",
      },
    ],
    exampleBrandsLine: "Built for skincare, makeup, personal care, clean beauty and wellness brands.",
    faq: [
      {
        q: "How can a beauty brand work with a community?",
        a: "With ernest, you offer products to try or sponsor a targeted community's self-care workshop, generating sincere reviews and authentic content from real users.",
      },
      {
        q: "Does community marketing work better than influencers?",
        a: "It's complementary: where an influencer placement is a one-off, a community offers peer recommendation, real trial and lasting word of mouth, all measured by ernest.",
      },
    ],
  },
  {
    slug: "fashion",
    label: "Fashion & Lifestyle",
    eyebrow: "ernest for fashion brands",
    metaTitle: "Community marketing for fashion & lifestyle brands · ernest",
    metaDescription:
      "ernest connects fashion and lifestyle brands with the IRL communities that make style: creative collectives, sneaker communities, lifestyle circles. Gifting, event sponsorship, ambassadors, content.",
    h1: "Community marketing for fashion & lifestyle brands",
    intro:
      "Fashion is validated inside a culture, not in an ad. ernest connects you to the communities that make style (creative collectives, sneaker communities, lifestyle circles) so you can run community sponsorships (gifting, event sponsorship, ambassadors), and measure the impact.",
    whyHeading: "Why fashion earns legitimacy in community",
    whyBody: [
      "A brand's desirability comes from being adopted by the right people in the right circles. Creative and lifestyle communities are where that cultural legitimacy is built.",
      "Being worn and embraced by a credible community is worth more than a placement: it's lived social proof that spreads naturally afterwards.",
    ],
    painPoints: [
      "Cultural legitimacy can't be bought with paid media.",
      "You want authentic adoption by the right circles, not just reach.",
      "You want credible content and social proof.",
    ],
    communityHeading: "Communities that fit your brand",
    communityTypes: [
      "Creative and artistic collectives",
      "Sneaker and streetwear communities",
      "Lifestyle and style circles",
      "Design and culture communities",
      "Urban lifestyle groups (running, stylish cycling, etc.)",
    ],
    activationHeading: "Sponsorship formats that work for fashion",
    activations: [
      {
        title: "Seeding & product drops",
        body: "Dress the members of a credible community and let the product show up in its natural context.",
      },
      {
        title: "Event sponsorship",
        body: "Attach your brand to a cultural or creative moment carried by a community.",
      },
      {
        title: "Ambassadors & content",
        body: "Work with community figures who embody your brand and create content.",
      },
    ],
    exampleBrandsLine: "Built for fashion, sneakers, accessories, eyewear and lifestyle brands.",
    faq: [
      {
        q: "How does a fashion brand gain credibility through communities?",
        a: "With ernest, your brand is adopted and worn by credible creative and lifestyle communities, creating lived social proof that spreads naturally, more durable than a one-off placement.",
      },
      {
        q: "What kind of lifestyle communities can I activate?",
        a: "Creative collectives, sneaker communities, design circles or urban lifestyle groups: ernest matches you with the ones whose culture fits your brand.",
      },
    ],
  },
  {
    slug: "wellness",
    label: "Wellness",
    eyebrow: "ernest for wellness brands",
    metaTitle: "Community marketing for wellness brands · ernest",
    metaDescription:
      "ernest connects wellness brands with the IRL communities that practice together: yoga, meditation, breathwork, recovery. Gifting, event sponsorship, ambassadors, content.",
    h1: "Community marketing for wellness brands",
    intro:
      "Wellness is lived in practice and in groups. ernest connects you to the communities that practice together (yoga, meditation, breathwork, recovery, sober-curious) so you can run community sponsorships (gifting, event sponsorship, ambassadors), and measure real impact.",
    whyHeading: "Why wellness is practiced in community",
    whyBody: [
      "Buying into a wellness brand runs on experience and trust. Practice communities gather people committed to a routine, open to products that support it.",
      "Recommended by a community of practitioners, a product gains credibility that advertising struggles to build in a market where trust is everything.",
    ],
    painPoints: [
      "Trust is everything, and it's hard to build with paid media.",
      "Your products need to be lived in a practice to convince.",
      "You want credible, committed advocates.",
    ],
    communityHeading: "Communities that fit your brand",
    communityTypes: [
      "Yoga studios and collectives",
      "Meditation and breathwork groups",
      "Recovery and cold-plunge communities",
      "Sober-curious and mental-health communities",
      "Pilates and mobility groups",
    ],
    activationHeading: "Sponsorship formats that work for wellness",
    activations: [
      {
        title: "Gifting & trials",
        body: "Fold your products (supplements, gear, functional drinks) into a real practice and collect committed feedback.",
      },
      {
        title: "Session & retreat sponsorship",
        body: "Fund or co-host a session or retreat around your brand.",
      },
      {
        title: "Ambassadors & content",
        body: "Work with facilitators and practitioners who recommend and create authentic content.",
      },
    ],
    exampleBrandsLine: "Built for supplements, functional nutrition, wellness gear, apps and recovery brands.",
    faq: [
      {
        q: "How can a wellness brand work with communities?",
        a: "With ernest, you fold your products into real practices (yoga, meditation, recovery) or sponsor sessions, earning credible recommendation from committed practitioner communities.",
      },
      {
        q: "How do you measure the return of a wellness activation?",
        a: "ernest tracks real community metrics: Genuine Interest Reach, cost per genuine interest, word-of-mouth conversion and trackable conversions, beyond impressions.",
      },
    ],
  },
  {
    slug: "tech",
    label: "Tech & Apps",
    eyebrow: "ernest for tech & app brands",
    metaTitle: "Community marketing for tech & app brands · ernest",
    metaDescription:
      "ernest connects tech and app brands with the IRL communities their users already belong to: run clubs, meetups, coworking, creative collectives. Seeding, event sponsorship, ambassadors, content.",
    h1: "Community marketing for tech and app brands",
    intro:
      "Apps spread by recommendation, not by impression. ernest connects you to the IRL communities your future users already belong to (run clubs for a fitness app, meetups for a dev tool, coworking and creative collectives for a productivity product) and gives you the workflow to run community sponsorships: seeding, event sponsorship, ambassador and content activations, then measure real impact.",
    whyHeading: "Why apps grow inside communities",
    whyBody: [
      "An install triggered by an ad is a stranger. An install recommended by someone in your group already comes with a reason to open it again tomorrow: communities deliver the context and the trust that turn a download into a habit.",
      "Every community is a ready-made segment of people who already do the thing your product serves. A run club is a room full of qualified users for a training app; a founders' meetup is one for a billing tool. You're not buying attention, you're getting adopted where usage already happens.",
    ],
    painPoints: [
      "Your paid acquisition costs keep climbing while installs get less qualified.",
      "Downloads are fine, but retention and activation are the real problem.",
      "Your product needs to be explained and tried, not just seen in a feed.",
    ],
    communityHeading: "Communities that fit your brand",
    communityTypes: [
      "Startup, founder and product meetups",
      "Developer, AI and maker communities",
      "Coworking spaces and freelance collectives",
      "Sport, wellness and hobby clubs matching your vertical",
      "Student associations and young-professional networks",
    ],
    activationHeading: "Sponsorship formats that work for tech",
    activations: [
      {
        title: "Seeding & premium access",
        body: "Give a community's members free premium access or credits, and get real usage feedback from people who fit your target profile.",
      },
      {
        title: "Meetup & event sponsorship",
        body: "Fund a community's meetup, hackathon or workshop in exchange for a demo slot, presence and a genuine conversation with users.",
      },
      {
        title: "Ambassadors & power users",
        body: "Turn organizers and power users into ambassadors who onboard their peers and create content that explains the product better than an ad.",
      },
    ],
    exampleBrandsLine:
      "Built for consumer apps, fitness and health apps, mobility, learning platforms, creator tools, developer tools and SaaS products.",
    faq: [
      {
        q: "How can an app acquire users through communities?",
        a: "With ernest, you identify communities whose members match your target, then offer premium access, sponsor a meetup or set up an ambassador partnership. Every collaboration is scoped and measured inside the platform, so you can compare it against your paid channels.",
      },
      {
        q: "Does community marketing work for B2B and SaaS products?",
        a: "Yes, with a different map. Instead of consumer clubs, you activate founders' clubs, developer and product meetups, coworking communities and professional networks: the places where your buyers already gather. ernest matches you with the ones whose members fit your ICP.",
      },
    ],
  },
  {
    slug: "finance",
    label: "Finance & Fintech",
    eyebrow: "ernest for finance, fintech & funds",
    metaTitle: "Community marketing for finance, fintech & funds · ernest",
    metaDescription:
      "ernest connects banks, fintechs, insurers and investment funds with the professional communities their clients belong to: founders' clubs, business breakfasts, coworking, freelance collectives.",
    h1: "Community marketing for finance and fintech brands",
    intro:
      "Financial decisions travel by recommendation between peers. ernest connects banks, fintechs, insurers and funds to the professional communities where those conversations happen (founders' clubs, business breakfasts, coworking spaces, freelance collectives) so you can run community sponsorships, teach and build relationships, then measure the impact.",
    whyHeading: "Why finance is chosen on trust",
    whyBody: [
      "Nobody switches bank, insurer or payment provider because of a banner. They switch because someone they respect said it works. Professional communities are exactly where that sentence gets spoken, repeatedly, to people in the same situation.",
      "These rooms also concentrate your highest-value segments: founders, freelancers, young professionals, people at the precise life or business moment when a financial product gets chosen. Showing up usefully there beats outbidding everyone on the same keywords.",
    ],
    painPoints: [
      "Acquisition costs are high and your category inspires low spontaneous trust.",
      "Your best clients come from referral, but you have no way to organize it.",
      "You want to reach founders, freelancers or young professionals at the right moment.",
    ],
    communityHeading: "Communities that fit your brand",
    communityTypes: [
      "Founders' clubs and startup communities",
      "Business breakfasts and professional networks",
      "Coworking spaces and freelance collectives",
      "Student and young-professional associations",
      "Angel, investor and alumni circles",
    ],
    activationHeading: "Sponsorship formats that work for finance",
    activations: [
      {
        title: "Club & event series sponsorship",
        body: "Back a founders' club, a breakfast series or a community's annual event, and become a familiar presence rather than an ad in their feed.",
      },
      {
        title: "Workshops & financial education",
        body: "Run a genuinely useful session for members (fundraising, cash management, taxes, insurance) where your expertise is the product being demonstrated.",
      },
      {
        title: "Ambassadors & member offers",
        body: "Work with organizers and members who recommend you to their peers, with a dedicated offer for the community and trackable conversions.",
      },
    ],
    exampleBrandsLine:
      "Built for neobanks, payment and billing fintechs, insurers and brokers, wealth and investment platforms, and VC funds sponsoring founder communities for dealflow and visibility.",
    faq: [
      {
        q: "Can a VC fund or investment firm use ernest?",
        a: "Yes. Funds sponsor founders' clubs, startup communities and angel circles the same way a brand sponsors a run club: to be present where founders gather, source dealflow and build reputation among the people they want to back. ernest handles the matching, the scoping and the measurement.",
      },
      {
        q: "How does a bank or fintech activate a professional community?",
        a: "By sponsoring the community's events, running a useful workshop for its members, or setting up an ambassador partnership with a dedicated member offer. ernest matches you with the communities whose members fit your target and tracks the results.",
      },
    ],
  },
];

export const getSectorBySlug = (slug: string | undefined): BrandSector | undefined =>
  BRAND_SECTORS.find((s) => s.slug === slug);
